import {AssistantTextBlock, AssistantTextLine, AssistantTextPart} from "../interfaces/assistant";

/** Пункт списку: «- », «• » чи «* » на початку рядка; відступ перед ним не важить (вкладених списків вікно не малює) */
const LIST_ITEM = /^\s*[-•*]\s+(\S.*)$/;

/** Крок нумерованого списку: «1. » чи «1) » на початку рядка */
const STEP_ITEM = /^\s*(\d{1,3})[.)]\s+(\S.*)$/;

/** Жирне - пара **…** в межах рядка; всередині не порожньо, без «*» і без пробілів біля зірочок */
const BOLD = /\*\*([^\s*](?:[^*]*[^\s*])?)\*\*/g;

/** Що може стояти в рядку-заголовку поза жирним: пробіли й двокрапка */
const HEADING_REST = /^[\s:]*$/;

/**
 * Картка #986: текст відповіді помічника блоками для вікна чату - абзаци (межа - порожній рядок), списки «- » і
 * нумеровані кроки «1. », жирне **…**; рядок, цілком жирний, - заголовок блоку. Розмітка тут лише ця: решта (таблиці,
 * «#», HTML, [текст](адреса), потрійні лапки) лишається звичайним текстом, а HTML вікно з тексту не будує - тільки шаблон
 * по цих блоках. links - шматки тексту з посиланнями на сторінки кабінету (AssistantActionsService.textParts).
 */
export function assistantTextBlocks(text: string, links: (text: string) => AssistantTextPart[]): AssistantTextBlock[] {
  const blocks: AssistantTextBlock[] = [];
  let current: AssistantTextBlock | null = null;
  // Перед цим рядком був порожній
  let gap = false;

  for (const raw of text.replace(/\r\n?/g, '\n').split('\n')) {
    if (raw.trim() === '') {
      gap = true;
      continue;
    }

    const listItem = raw.match(LIST_ITEM);
    const stepItem = listItem ? null : raw.match(STEP_ITEM);
    const item = listItem ? listItem[1] : stepItem ? stepItem[2] : null;
    const line = lineParts(item !== null ? item : raw.trim(), links);

    if (item !== null) {
      const type = listItem ? 'list' : 'steps';
      let items = current && current.type === type ? current.items : undefined;

      // Пункт того ж списку - і через порожній рядок; інший вид - новий список
      if (!items) {
        items = [];
        current = stepItem ? {type, items, start: Number(stepItem[1])} : {type, items};
        blocks.push(current);
      }

      items.push([line]);
    } else if (current && current.items && !gap && /^\s/.test(raw)) {
      // Рядок з відступом одразу під пунктом - продовження пункту
      current.items[current.items.length - 1].push(line);
    } else if (current && current.lines && !gap) {
      current.lines.push(line);
    } else {
      current = {type: 'paragraph', lines: [line]};
      blocks.push(current);
    }

    gap = false;
  }

  // Перший рядок абзацу цілком жирний - заголовок: окремим блоком, а решта рядків - абзацом під ним
  return blocks.reduce((result: AssistantTextBlock[], block) => {
    if (block.type !== 'paragraph' || !block.lines || !isHeading(block.lines[0])) {
      result.push(block);
    } else {
      result.push({type: 'paragraph', lines: [block.lines[0]], heading: true});

      if (block.lines.length > 1) {
        result.push({type: 'paragraph', lines: block.lines.slice(1)});
      }
    }

    return result;
  }, []);
}

/** Рядок шматками: жирне за парами **…**, посилання - всередині і жирного, і звичайного тексту */
function lineParts(line: string, links: (text: string) => AssistantTextPart[]): AssistantTextLine {
  const parts: AssistantTextPart[] = [];
  let last = 0;

  for (const match of line.matchAll(BOLD)) {
    const start = match.index ?? 0;

    if (start > last) {
      parts.push(...links(line.slice(last, start)));
    }

    parts.push(...links(match[1]).map((part) => ({...part, bold: true})));
    last = start + match[0].length;
  }

  if (last < line.length) {
    parts.push(...links(line.slice(last)));
  }

  return parts;
}

/** Увесь рядок жирний (поза жирним - хіба пробіли чи двокрапка) */
function isHeading(line: AssistantTextLine): boolean {
  return line.some((part) => part.bold) && line.every((part) => part.bold || HEADING_REST.test(part.text));
}
